import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input,
	input,
	OnInit,
	Output,
	output,
	PLATFORM_ID,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import {
	BehaviorSubject,
	debounceTime,
	filter,
	Observable,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

import {
	AvailabilityDatesInterface,
	AvailabilityRequestInterface,
} from '@valk-nx/availability-dates-store/availability-dates.interface';
import { AvailabilityDatesService } from '@valk-nx/availability-dates-store/availability-dates.service';
import { cn, fallbackLanguage } from '@valk-nx/core/lib/core';
import {
	HotelInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { toDate } from '@valk-nx/helpers/lib/transformers/toDate';
import { PackageDealInterface } from '@valk-nx/packages-store/packages.interface';
import { RouteHelper } from '@valk-nx/router-store/router.helper';

import { AvailabilityWidgetInputDateRangeComponent } from './availability-input-date-range/availability-input-date-range';
import { AvailabilityWidgetInputDropdownComponent } from './availability-input-dropdown/availability-input-dropdown';
import { AvailabilityInputDropdownItem } from './availability-input-dropdown/availability-input-dropdown.interface';
import { AvailabilityWidgetInputOccupancyComponent } from './availability-input-occupancy/availability-input-occupancy';

export interface WidgetData {
	numRooms: number;
	numNights: number;
	selectedArrivalDate: string;
	selectedDepartureDate: string;
	price: number;
	currency: string;
	rooms: OccupancyInterface[];
}

export interface WidgetDataActionInterface {
	dayTypeBooking: string;
	dayTypeVisit: string;
	dealGUID?: string;
	hotelGUID: string;
	leadtime: number;
	numRooms: number;
	numNights: number;
	numGuests: number;
	packageGUID?: string;
	roomGUID?: string;
	rooms: {
		item_name: string;
		numAdults: number;
		numChildren: number;
		numBabies: number;
	}[];
	selectedArrivalDate: string;
	selectedDepartureDate: string;
}

export interface AvailabilityWidgetEmitInterface {
	arrivalDate: Date;
	departureDate: Date;
	occupancy: OccupancyInterface[];
	hotel: Partial<HotelInterface>;
	roomGUID: string;
	dealGUID: string;
	packageGUID: string;
}

const yesterday = dayjs().subtract(1, 'day').toDate();

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget`,
	templateUrl: './availability-widget.component.html',
	imports: [
		AvailabilityWidgetInputDateRangeComponent,
		AvailabilityWidgetInputOccupancyComponent,
		AvailabilityWidgetInputDropdownComponent,
		AsyncPipe,
		CommonModule,
		TranslatePipe,
	],
})
export class AvailabilityWidgetComponent implements OnInit {
	@Input() roomGUID: string | undefined;
	@Input() dealGUID: string | undefined;
	@Input() packageGUID: string | undefined;
	@Input() accommodationTypes: string[] | undefined;
	@Input() hotelGUID: string;
	@Input() defaultAdults = 2;
	@Input() occupancyRestrictions: OccupancyRestrictionsInterface = {
		adults: {
			min: 1,
			max: 3,
		},
		children: {
			min: 0,
			max: 1,
		},
		infants: {
			min: 0,
			max: 1,
		},
		maxPersons: 2,
		maxOccupancies: 5,
	};
	@Input() maxDate = dayjs().add(1, 'year').toDate();
	@Input() minDate = yesterday;
	@Input({ required: true }) bookingtoolUrl: string;
	@Input({ transform: toDate }) initialArrivalDate: Date;
	@Input() initialOccupancy: OccupancyInterface[];
	@Input() hotels: AvailabilityInputDropdownItem[] = [];
	@Input({ required: true }) language: string = fallbackLanguage;
	@Input() useCustomEmitter = false;
	@Output() customEmitter: EventEmitter<AvailabilityWidgetEmitInterface> =
		new EventEmitter<AvailabilityWidgetEmitInterface>();

	showHotels = input(false);
	showOccupancy = input(true);
	showDates = input(true);
	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	showPackages = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);

	occupancy: OccupancyInterface[];

	updateAvailability$ = new BehaviorSubject<AvailabilityRequestInterface>(
		undefined,
	);
	isLoading = false;
	jumpToFirstAvailableDate = true;

	availability$: Observable<AvailabilityDatesInterface>;
	firstDateInCalendar: string = dayjs().format('YYYY-MM-DD');
	isOccupancyOpen = false;
	isHotelsOpen = false;
	isPackagesOpen = false;
	isDatePickerOpen = false;
	selectedHotel: Partial<HotelInterface>;
	selectedPackage: Partial<PackageDealInterface>;

	arrivalDate: Date;
	departureDate: Date;
	_resetDateRange = 0;

	cn = cn;

	constructor(
		private readonly availability: AvailabilityDatesService,
		@Inject(PLATFORM_ID) readonly platformId: string,
	) {
		this.availability$ = this.updateAvailability$.pipe(
			debounceTime(300),
			filter((request) => !!request && !!request.hotelGUID),
			switchMap((request) =>
				this.availability.getAvailabilityDates(
					request.hotelGUID,
					request.endDate,
					request.startDate,
					request.occupancies,
					request.accommodationTypes,
					request.dealGUID,
					request.packageGUID,
					request.roomGUID,
				),
			),
			tap(() => (this.isLoading = false)),
			shareReplay(1),
		);
	}

	ngOnInit() {
		if (this.minDate && this.minDate.toString() !== yesterday.toString()) {
			this.firstDateInCalendar = dayjs(this.minDate).format('YYYY-MM-DD');
		}
		if (
			this.initialArrivalDate &&
			this.minDate.toString() === yesterday.toString()
		) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		if (!this.showOccupancy()) {
			this.occupancy = [
				{ adults: this.defaultAdults, children: 0, infants: 0 },
			];
		}
		this.getAvailability();
	}

	onChooseDatesEmit(event: Event) {
		this.isOccupancyOpen = false;
		this.isDatePickerOpen = true;

		this.getAvailability();

		event.stopPropagation();
	}

	onBackButton(
		event: Event,
		isPackagesOpen: boolean,
		isHotelsOpen: boolean,
		isOccupancyOpen: boolean,
		isDatePickerOpen: boolean,
	) {
		this.isPackagesOpen = isPackagesOpen;
		this.isHotelsOpen = isHotelsOpen;
		this.isOccupancyOpen = isOccupancyOpen;
		this.isDatePickerOpen = isDatePickerOpen;

		event.stopPropagation();
	}

	onDatePickerOpened(isOpen: boolean) {
		this.isDatePickerOpen = isOpen;
	}

	onOccupancyOpenEmit(isOpen: boolean) {
		this.isOccupancyOpen = isOpen;
	}

	onOccupancyEmit(occupancy: OccupancyInterface[]) {
		this.occupancy = occupancy;
		this.getAvailability();
	}

	onHotelSelect(hotel: Partial<HotelInterface>) {
		this.selectedHotel = hotel;
		this.getAvailability();
	}

	onPackageSelect(packageItem: Partial<PackageDealInterface>) {
		this.selectedPackage = packageItem;
		this.resetDateRange();
		this.getAvailability();
	}

	resetDateRange() {
		this._resetDateRange++;
	}

	recalculateAvailabilityForToday() {
		this.firstDateInCalendar = dayjs()
			.startOf('month')
			.format('YYYY-MM-DD');
		this.getAvailability();
	}

	onMonthChange(newFirstDate: string) {
		this.firstDateInCalendar = newFirstDate;

		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
	}

	getAvailability() {
		if (isPlatformBrowser(this.platformId)) {
			const request: AvailabilityRequestInterface = {
				accommodationTypes: this.accommodationTypes,
				hotelGUID: this.hotelGUID
					? this.hotelGUID
					: this.selectedHotel?.guid,
				startDate: this.firstDateInCalendar,
				endDate: dayjs(this.firstDateInCalendar)
					.add(2, 'month')
					.format('YYYY-MM-DD'),
				occupancies: this.occupancy,
				dealGUID: this.dealGUID,
				packageGUID: this.showPackages()
					? this.packageGUID || this.selectedPackage?.GUID
					: undefined,
				roomGUID: this.roomGUID,
			};

			this.isLoading = true;
			this.updateAvailability$.next(request);
		}
	}

	updateModel(event) {
		this.arrivalDate = event.arrivalDate;
		this.departureDate = event.departureDate;
	}

	goToBookingtool() {
		if (this.useCustomEmitter) {
			this.customEmitter.emit({
				arrivalDate: this.arrivalDate,
				departureDate: this.departureDate,
				occupancy: this.occupancy,
				hotel: this.selectedHotel,
				roomGUID: this.roomGUID,
				dealGUID: this.dealGUID,
				packageGUID: this.showPackages()
					? this.packageGUID || this.selectedPackage?.GUID
					: undefined,
			});
			return;
		}

		if (this.bookingtoolUrl) {
			const languageCode = `&languageCode=${this.language}`;
			const arrivalString = this.arrivalDate
				? `&startDate=${dayjs(this.arrivalDate).format('YYYY-MM-DD')}`
				: '';
			const departureString = this.departureDate
				? `&endDate=${dayjs(this.departureDate).format('YYYY-MM-DD')}`
				: '';
			const occupancyString = this.occupancy
				? `&occupancy=${JSON.stringify(this.occupancy)}`
				: '';
			const roomString = this.roomGUID
				? `&roomGUID=${this.roomGUID}`
				: '';
			const dealString = this.dealGUID
				? `&dealGUID=${this.dealGUID}`
				: '';
			const packageString = this.showPackages()
				? `&packageGUID=` +
					(this.packageGUID || this.selectedPackage?.GUID)
				: '';
			const parameters = `${arrivalString}${departureString}${occupancyString}${roomString}${dealString}${packageString}`;

			const hotelurl = this.selectedHotel
				? `?hotelSlug=${this.selectedHotel.slug}`
				: '';

			this.setEmittedData();

			RouteHelper.redirectToExternalUrl(
				`${this.bookingtoolUrl}${hotelurl}${languageCode}${parameters}`,
			);
		}
	}

	setEmittedData() {
		const eventType = () => {
			if (this.packageGUID) {
				return 'packages';
			} else if (this.roomGUID) {
				return 'rooms';
			} else if (this.dealGUID) {
				return 'deals';
			} else {
				return 'default';
			}
		};
		const rooms = this.occupancy.map((occupancy, i) => ({
			item_name: `ROOM${i + 1}`,
			numAdults: occupancy.adults,
			numChildren: occupancy.children,
			numBabies: occupancy.infants,
		}));
		const dayTypeBooking =
			dayjs().day() === 0 || dayjs().day() === 6 ? 'weekend' : 'weekday';
		const dayTypeVisit =
			dayjs(this.arrivalDate).day() === 5 ||
			dayjs(this.arrivalDate).day() === 6
				? 'weekend'
				: 'weekday';

		this.actionClick.emit({
			event: 'search_availability',
			eventType: eventType(),
			data: {
				dayTypeBooking,
				dayTypeVisit,
				dealGUID: this.dealGUID,
				hotelGUID: this.hotelGUID,
				leadtime: dayjs(this.arrivalDate).diff(
					dayjs().format('YYYY-MM-DD'),
					'day',
				),
				numGuests: this.occupancy.reduce((acc, curr) => {
					return acc + curr.adults + curr.children + curr.infants;
				}, 0),
				numRooms: this.occupancy.length,
				numNights: dayjs(this.departureDate).diff(
					dayjs(this.arrivalDate),
					'days',
				),
				packageGUID: this.packageGUID,
				roomGUID: this.roomGUID,
				rooms,
				selectedArrivalDate: dayjs(this.arrivalDate).format(
					'YYYY-MM-DD',
				),
				selectedDepartureDate: dayjs(this.departureDate).format(
					'YYYY-MM-DD',
				),
			},
		});
	}
}
